import { useState, useCallback, useRef, useEffect } from 'react';
import { RaceResult, TextPassage } from '../types/GameTypes';
import { getGuestId } from '../utils/storage';

export type RoomMode = 'casual' | 'ranked';

export interface PlayerInfo {
  name: string;
  color: string;
  isCreator: boolean;
  userId: string | null;
  isGuest: boolean;
}

export interface PlayerProgress {
  name: string;
  color: string;
  currentIndex: number;
  totalLength: number;
  wpm: number;
  finished: boolean;
}

export interface PlayerResult {
  name: string;
  color: string;
  rank: number;
  result: RaceResult;
}

export type MultiplayerState = 'disconnected' | 'lobby' | 'countdown' | 'racing' | 'finished';

const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
const WS_URL = process.env.NODE_ENV === 'production'
  ? `${wsProtocol}//${window.location.host}`
  : `ws://${window.location.hostname}:3001`;

export function useMultiplayer() {
  const [state, setState] = useState<MultiplayerState>('disconnected');
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [roomMode, setRoomMode] = useState<RoomMode>('casual');
  const [players, setPlayers] = useState<PlayerInfo[]>([]);
  const [playerProgress, setPlayerProgress] = useState<PlayerProgress[]>([]);
  const [raceResults, setRaceResults] = useState<PlayerResult[]>([]);
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const [passage, setPassage] = useState<TextPassage | null>(null);
  const [isCreator, setIsCreator] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rematchVoters, setRematchVoters] = useState<string[]>([]);
  const [rematchSecondsLeft, setRematchSecondsLeft] = useState<number | null>(null);

  const wsRef = useRef<WebSocket | null>(null);

  const send = useCallback((msg: object) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const connect = useCallback((): Promise<WebSocket> => {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => resolve(ws);
      ws.onerror = () => reject(new Error('Connection failed'));

      ws.onmessage = (event) => {
        const msg = JSON.parse(event.data as string);

        switch (msg.type) {
          case 'room-created':
            setRoomCode(msg.roomCode);
            setPassage(msg.passage);
            setRoomMode(msg.mode || 'casual');
            setState('lobby');
            break;
          case 'player-joined':
            setPlayers(msg.players);
            break;
          case 'player-left':
            setPlayers(msg.players);
            break;
          case 'countdown':
            setState('countdown');
            setCountdownSeconds(msg.seconds);
            break;
          case 'race-start':
            setState('racing');
            setPassage(msg.passage);
            setRematchVoters([]);
            setRematchSecondsLeft(null);
            break;
          case 'progress-update':
            setPlayerProgress(msg.players);
            break;
          case 'player-finished':
            break;
          case 'race-end':
            setState('finished');
            setRaceResults(msg.results);
            break;
          case 'rematch-request':
            setRematchVoters(msg.accepted);
            break;
          case 'rematch-countdown':
            setRematchVoters(msg.voters);
            setRematchSecondsLeft(msg.secondsLeft);
            break;
          case 'error':
            setError(msg.message);
            break;
        }
      };

      ws.onclose = () => {
        setState('disconnected');
        setRoomCode(null);
        setPlayers([]);
      };
    });
  }, []);

  const createRoom = useCallback(async (playerName: string, difficulty: string, authToken?: string | null, mode: RoomMode = 'casual') => {
    try {
      setError(null);
      await connect();
      setIsCreator(true);
      setTimeout(() => {
        const msg: Record<string, unknown> = { type: 'create', playerName, difficulty, mode, guestId: getGuestId() };
        if (authToken) msg.authToken = authToken;
        send(msg);
      }, 100);
    } catch {
      setError('Could not connect to server');
    }
  }, [connect, send]);

  const joinRoom = useCallback(async (playerName: string, code: string, authToken?: string | null) => {
    try {
      setError(null);
      await connect();
      setIsCreator(false);
      setTimeout(() => {
        const msg: Record<string, unknown> = { type: 'join', roomCode: code, playerName, guestId: getGuestId() };
        if (authToken) msg.authToken = authToken;
        send(msg);
      }, 100);
    } catch {
      setError('Could not connect to server');
    }
  }, [connect, send]);

  const startRaceMP = useCallback(() => send({ type: 'start' }), [send]);
  const sendProgress = useCallback((currentIndex: number, errors: number, wpm: number) => {
    send({ type: 'progress', currentIndex, errors, wpm });
  }, [send]);
  const sendFinished = useCallback((result: RaceResult) => {
    send({ type: 'finished', result });
  }, [send]);
  const requestRematch = useCallback(() => send({ type: 'rematch' }), [send]);

  const leave = useCallback(() => {
    send({ type: 'leave' });
    wsRef.current?.close();
    setState('disconnected');
    setRoomCode(null);
    setRoomMode('casual');
    setPlayers([]);
    setPlayerProgress([]);
    setRaceResults([]);
    setPassage(null);
    setIsCreator(false);
  }, [send]);

  useEffect(() => {
    return () => {
      wsRef.current?.close();
    };
  }, []);

  return {
    state, roomCode, roomMode, players, playerProgress, raceResults,
    countdownSeconds, passage, isCreator, error,
    rematchVoters, rematchSecondsLeft,
    createRoom, joinRoom, startRace: startRaceMP,
    sendProgress, sendFinished, requestRematch, leave,
  };
}
